import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  bulk_moderate_listings,
  fetch_adoption_counts,
  fetch_adoption_detail,
  fetch_adoption_list,
  moderate_listing,
  fetch_adoption_refunds,
  set_adoption_adopter,
  refund_adoption_participant,
  refund_all_adoption_participants,
} from './api';
import type {
  AdoptionListFilters,
  AdoptionModerationAction,
  AdoptionRefundView,
} from './types';
import type { SortState } from '@/hooks/use-url-state';

const KEYS = {
  list: (filters: AdoptionListFilters, sort?: SortState) =>
    ['admin-adoption', 'list', filters, sort] as const,
  counts: () => ['admin-adoption', 'counts'] as const,
  detail: (id: string | undefined) => ['admin-adoption', 'detail', id] as const,
  refunds: (id: string | undefined) => ['admin-adoption', 'refunds', id] as const,
};

export function use_adoption_list(filters: AdoptionListFilters, sort?: SortState) {
  return useQuery({
    queryKey: KEYS.list(filters, sort),
    queryFn: () => fetch_adoption_list(filters, sort),
    placeholderData: keepPreviousData,
  });
}

export function use_adoption_counts() {
  return useQuery({ queryKey: KEYS.counts(), queryFn: fetch_adoption_counts });
}

export function use_adoption_detail(id: string | undefined) {
  return useQuery({
    queryKey: KEYS.detail(id),
    queryFn: () => fetch_adoption_detail(id as string),
    enabled: !!id,
  });
}

/**
 * Invalidate everything a moderation decision moves.
 *
 * The tiles are per-status counts and the segments are status filters, so a
 * hide changes three surfaces at once — leaving any of them stale means the
 * queue tells an operator there is work that is already done.
 */
function use_invalidate_adoption(id?: string) {
  const qc = useQueryClient();
  return () => {
    if (id) void qc.invalidateQueries({ queryKey: KEYS.detail(id) });
    void qc.invalidateQueries({ queryKey: ['admin-adoption', 'list'] });
    void qc.invalidateQueries({ queryKey: KEYS.counts() });
  };
}

export function use_moderate_listing(id: string) {
  const invalidate = use_invalidate_adoption(id);
  return useMutation({
    mutationFn: ({ action, reason }: { action: AdoptionModerationAction; reason: string }) =>
      moderate_listing(id, action, reason),
    onSuccess: invalidate,
  });
}

export function use_bulk_moderate_listings() {
  const invalidate = use_invalidate_adoption();
  return useMutation({
    mutationFn: ({
      listing_ids,
      action,
      reason,
    }: {
      listing_ids: string[];
      action: AdoptionModerationAction;
      reason: string;
    }) => bulk_moderate_listings(listing_ids, action, reason),
    onSuccess: invalidate,
  });
}

/**
 * Who is owed their adoption fee back on this listing.
 *
 * Its own query rather than part of the detail: it joins payments and refund
 * state, and the moderation panels have no use for that.
 */
export function use_adoption_refunds(id: string | undefined) {
  return useQuery({
    queryKey: KEYS.refunds(id),
    queryFn: () => fetch_adoption_refunds(id as string),
    enabled: !!id,
  });
}

/**
 * Everything that changes who is owed money.
 *
 * Each of these returns the fresh view, so the cache is SET from the response
 * rather than refetched — the refund state is the whole point of the screen and a
 * refetch round-trip is a visible stale flash on a financial number. The detail is
 * invalidated too: it carries `fees_collected_inr`, which a refund moves.
 */
function use_refund_mutation<TArgs>(
  id: string | undefined,
  fn: (args: TArgs) => Promise<AdoptionRefundView>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (view: AdoptionRefundView) => {
      qc.setQueryData(KEYS.refunds(id), view);
      void qc.invalidateQueries({ queryKey: KEYS.detail(id) });
    },
  });
}

export function use_set_adoption_adopter(id: string | undefined) {
  return use_refund_mutation<string | null>(id, (request_id) =>
    set_adoption_adopter(id as string, request_id),
  );
}

export function use_refund_adoption_participant(id: string | undefined) {
  return use_refund_mutation<string>(id, (request_id) =>
    refund_adoption_participant(id as string, request_id),
  );
}

/** The batch. Returns per-person outcomes, so a partial failure is reportable. */
export function use_refund_all_adoption_participants(id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => refund_all_adoption_participants(id as string),
    onSuccess: (result) => {
      qc.setQueryData(KEYS.refunds(id), result.view);
      void qc.invalidateQueries({ queryKey: KEYS.detail(id) });
    },
  });
}
